import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import LoadingIndicator from '../../ui/LoadingIndicator';

/**
 * Protected route component that handles both authenticated and anonymous users,
 * ensuring child profiles exist in both cases.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Protected content to render
 * @param {boolean} props.requireChildProfile - Whether to require a child profile
 * @param {boolean} props.allowAnonymous - Whether to allow anonymous access
 * @param {Function} props.navigateComponent - Optional custom Navigate component (for testing)
 */
const ProtectedRoute = ({ 
  children, 
  requireChildProfile = false, 
  allowAnonymous = false,
  navigateComponent = Navigate // Allow injection of a custom Navigate component for testing
}) => {
  const { isAuthenticated, loading, activeChildProfile, anonymousActivity } = useAuth();
  const location = useLocation();
  const NavigateComponent = navigateComponent; // Assign to uppercase variable for JSX usage
  
  // Show loading indicator while checking authentication
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        height: '100vh'
      }}>
        <LoadingIndicator />
      </div>
    );
  }
  
  // Handle anonymous users
  if (!isAuthenticated) {
    // If anonymous access is not allowed, redirect to login
    if (!allowAnonymous) {
      return (
        <NavigateComponent 
          to="/accounts/login/" 
          state={{ from: location }} 
          replace 
        />
      );
    }
    
    // Anonymous users with profile requirements will have direct access
    // No temporary profile checking needed
    return children;
  }
  
  // For authenticated users, ensure they have a child profile if required
  if (requireChildProfile && !activeChildProfile) {
    return (
      <NavigateComponent 
        to="/child-profiles/create/"
        state={{ from: location }}
        replace
      />
    );
  }
  
  // User is authenticated with all requirements met
  return children;
};

export default ProtectedRoute;